

// UserViewingRequest.jsx


import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  List,
  Card,
  CardContent,
  CardActions,
  Button,
  Skeleton,
  Divider,
  Tabs,
  Tab,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

function parseViewingDate(viewing) {
  if (viewing.dateDetails?.startTime) {
    const d = new Date(viewing.dateDetails.startTime);
    if (!isNaN(d.getTime())) return d;
  }
  const display = viewing.dateDetails?.displayText;
  if (display && display.startsWith("Specific Date:")) {
    const match = display.match(
      /Specific Date:\s*([A-Za-z]+ \d{1,2}, \d{4}),\s*from\s*([0-9:]+ [APMapm]{2})/
    );
    if (match) {
      const datePart = match[1];
      const timePart = match[2];
      const parseStr = `${datePart} ${timePart}`;
      const d = new Date(parseStr);
      if (!isNaN(d.getTime())) return d;
    }
  }
  if (viewing.date) {
    const d = new Date(viewing.date);
    if (!isNaN(d.getTime())) return d;
  }
  return null;
}

const UserViewingRequest = ({ scheduledViewings, loading }) => {
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState("approved"); // Default to approved tab
  const lastLogTime = useRef(0);

  // Batch log ALL tickets every 10 minutes, or on mount
  useEffect(() => {
    const logAll = () => {
      const now = Date.now();
      if (now - lastLogTime.current > 600000 || lastLogTime.current === 0) {
        lastLogTime.current = now;
        const allTickets = (scheduledViewings || []).map((v) => ({
          id: v.id,
          status: v.status,
          displayText: v?.dateDetails?.displayText,
          startTime: v?.dateDetails?.startTime,
          date: v.date,
          zoom: v.zoom,
        }));
        console.log("[DEBUG] ALL USER TICKETS (every 10min):", allTickets);
      }
    };
    logAll();
    const interval = setInterval(logAll, 600000); // every 10 min
    return () => clearInterval(interval);
  }, [scheduledViewings]);

  const handleViewDetails = (id) => {
    navigate(`/listing/${id}`);
  };

  const getDisplayDateTime = (viewing) => {
    if (viewing.dateDetails?.displayText) {
      return viewing.dateDetails.displayText;
    }
    const date = viewing.date || "Date TBD";
    const time = viewing.time || "";
    return `${date} ${time && `| ${time}`}`;
  };

  const getStatusColor = (status) => {
    const normalizedStatus = (status || "Pending").toLowerCase();
    switch (normalizedStatus) {
      case "approved":
        return "success.main";
      case "denied":
        return "error.main";
      // case "pending":
      default:
        return "warning.main";
    }
  };

  const filteredViewings = scheduledViewings
    .filter((viewing) => {
      const status = (viewing.status || "pending").toLowerCase();
      // if (currentTab === "pending") return status === "pending"; // Commented out - pending tab disabled
      if (currentTab === "approved") return status === "approved";
      if (currentTab === "denied") return status === "denied";
      return false;
    })
    .sort((a, b) => {
      // Sort by creation time (submittedAt) - newest first
      const timeA = a.submittedAt || 0;
      const timeB = b.submittedAt || 0;
      return timeB - timeA; // descending (newest first)
    });

  const now = new Date();

  const upcomingViewings = filteredViewings
    .filter((viewing) => {
      const d = parseViewingDate(viewing);
      // Include viewings without parseable dates as "upcoming" (they're likely pending scheduling)
      return !d || d >= now;
    })
    .sort((a, b) => {
      // Sort by creation time (submittedAt) - newest first
      const timeA = a.submittedAt || 0;
      const timeB = b.submittedAt || 0;
      return timeB - timeA;
    });

  const pastViewings = filteredViewings
    .filter((viewing) => {
      const d = parseViewingDate(viewing);
      return d && d < now;
    })
    .sort((a, b) => {
      // Sort by creation time (submittedAt) - newest first
      const timeA = a.submittedAt || 0;
      const timeB = b.submittedAt || 0;
      return timeB - timeA;
    });

  return (
    <Box sx={{ mt: 4, px: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: "bold", color: "primary.main", mb: 3 }}>
        Your Scheduled Viewings
      </Typography>
      <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
        {/* <Tab label="Pending" value="pending" /> */}
        <Tab label="Approved" value="approved" />
        <Tab label="Denied" value="denied" />
      </Tabs>
      {loading ? (
        [...Array(3)].map((_, index) => (
          <Skeleton
            key={`skeleton-${index}`}
            variant="rectangular"
            height={120}
            sx={{ mb: 2, borderRadius: 2 }}
          />
        ))
      ) : filteredViewings.length === 0 ? (
        <Typography sx={{ color: "text.secondary", fontStyle: "italic", mt: 2 }}>
          No viewings for this status
        </Typography>
      ) : (
        <>
          {/* Show all viewings if no date parsing worked, or show by date sections */}
          {upcomingViewings.length === 0 && pastViewings.length === 0 && filteredViewings.length > 0 ? (
            <List sx={{ maxWidth: 600 }}>
              {filteredViewings.map((viewing) => (
                <Card
                  key={viewing.id}
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                    boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
                    transition: "transform 0.2s",
                    "&:hover": { transform: "translateY(-2px)" },
                  }}
                >
                  <CardContent sx={{ py: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                      {viewing.listingTitle || "Untitled Listing"}
                    </Typography>
                    <Typography sx={{ color: "text.secondary", mb: 0.5 }}>
                      📍 {viewing.address || "Address TBD"}, {viewing.city || ""}{" "}
                      {viewing.state || ""}
                    </Typography>
                    <Typography sx={{ color: "text.secondary", mb: 0.5 }}>
                      🗓 {getDisplayDateTime(viewing)}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: "medium",
                        color: getStatusColor(viewing.status),
                      }}
                    >
                      Status: {viewing.status || "pending"}
                    </Typography>
                    {viewing.status === "approved" && viewing.zoom?.joinUrl && (
                      <Box sx={{ mt: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          href={viewing.zoom.joinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ textTransform: "none" }}
                        >
                          Join Zoom Meeting
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                  <Divider />
                  <CardActions sx={{ p: 1 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => handleViewDetails(viewing.listingId)}
                      sx={{ borderRadius: 1 }}
                    >
                      View Listing
                    </Button>
                  </CardActions>
                </Card>
              ))}
            </List>
          ) : (
            <>
              {/* Upcoming Viewings */}
              {upcomingViewings.length > 0 && (
            <List sx={{ maxWidth: 600 }}>
              {upcomingViewings.map((viewing) => (
                <Card
                  key={viewing.id}
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    background: "linear-gradient(135deg, #ffffff 0%, #f9f9f9 100%)",
                    boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
                    transition: "transform 0.2s",
                    "&:hover": { transform: "translateY(-2px)" },
                  }}
                >
                  <CardContent sx={{ py: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                      {viewing.listingTitle || "Untitled Listing"}
                    </Typography>
                    <Typography sx={{ color: "text.secondary", mb: 0.5 }}>
                      📍 {viewing.address || "Address TBD"}, {viewing.city || ""}{" "}
                      {viewing.state || ""}
                    </Typography>
                    <Typography sx={{ color: "text.secondary", mb: 0.5 }}>
                      🗓 {getDisplayDateTime(viewing)}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: "medium",
                        color: getStatusColor(viewing.status),
                      }}
                    >
                      Status: {viewing.status || "Pending"}
                    </Typography>
                    {viewing.status === "approved" && viewing.zoom?.joinUrl && (
                      <Box sx={{ mt: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          href={viewing.zoom.joinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ textTransform: "none" }}
                        >
                          Join Zoom Meeting
                        </Button>
                        {viewing.zoom.startTime && (
                          <Typography sx={{ fontSize: "0.88rem", mt: 0.5, color: "text.secondary" }}>
                            Starts: {new Date(viewing.zoom.startTime).toLocaleString()}
                          </Typography>
                        )}
                        {viewing.zoom.meetingId && (
                          <Typography sx={{ fontSize: "0.88rem", color: "text.secondary" }}>
                            Meeting ID: {viewing.zoom.meetingId}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </CardContent>
                  <Divider />
                  <CardActions sx={{ p: 1 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => handleViewDetails(viewing.listingId)}
                      sx={{ borderRadius: 1 }}
                    >
                      View Listing
                    </Button>
                  </CardActions>
                </Card>
              ))}
            </List>
          )}
          {/* Past Viewings */}
          {pastViewings.length > 0 && (
            <Box sx={{ mt: 6 }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", mb: 3, color: "primary.main" }}
              >
                Past {currentTab.charAt(0).toUpperCase() + currentTab.slice(1)} Viewings
              </Typography>
              <List sx={{ maxWidth: 600 }}>
                {pastViewings.map((viewing) => (
                  <Card
                    key={viewing.id}
                    sx={{
                      mb: 2,
                      borderRadius: 2,
                      background: "linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)",
                      boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
                    }}
                  >
                    <CardContent sx={{ py: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                        {viewing.listingTitle || "Untitled Listing"}
                      </Typography>
                      <Typography sx={{ color: "text.secondary", mb: 0.5 }}>
                        📍 {viewing.address || "Address TBD"}, {viewing.city || ""}{" "}
                        {viewing.state || ""}
                      </Typography>
                      <Typography sx={{ color: "text.secondary", mb: 0.5 }}>
                        🗓 {getDisplayDateTime(viewing)}
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: "medium",
                          color: getStatusColor(viewing.status),
                        }}
                      >
                        Status: {viewing.status || "Pending"}
                      </Typography>
                      {viewing.status === "approved" && viewing.zoom?.joinUrl && (
                        <Box sx={{ mt: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            href={viewing.zoom.joinUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ textTransform: "none" }}
                          >
                            Join Zoom Meeting
                          </Button>
                          {viewing.zoom.startTime && (
                            <Typography sx={{ fontSize: "0.88rem", mt: 0.5, color: "text.secondary" }}>
                              Starts: {new Date(viewing.zoom.startTime).toLocaleString()}
                            </Typography>
                          )}
                          {viewing.zoom.meetingId && (
                            <Typography sx={{ fontSize: "0.88rem", color: "text.secondary" }}>
                              Meeting ID: {viewing.zoom.meetingId}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </List>
            </Box>
          )}
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default UserViewingRequest;




// import React, { useState } from "react";
// import {
//   Box,
//   Typography,
//   List,
//   Card,
//   CardContent,
//   CardActions,
//   Button,
//   Skeleton,
//   Divider,
//   Tabs,
//   Tab,
// } from "@mui/material";
// import { useNavigate } from "react-router-dom";

// const UserViewingRequest = ({ scheduledViewings, loading }) => {
//   const navigate = useNavigate();
//   const [currentTab, setCurrentTab] = useState("pending");

//   const handleViewDetails = (id) => {
//     navigate(`/listing/${id}`);
//   };

//   // Extracts display-friendly date/time from new format
//   const getDisplayDateTime = (viewing) => {
//     // Prefer new format
//     if (viewing.dateDetails?.displayText) {
//       return viewing.dateDetails.displayText;
//     }
//     // Fallback: legacy fields
//     const date = viewing.date || "Date TBD";
//     const time = viewing.time || "";
//     return `${date} ${time && `| ${time}`}`;
//   };

//   // For status pill color
//   const getStatusColor = (status) => {
//     const normalizedStatus = (status || "Pending").toLowerCase();
//     switch (normalizedStatus) {
//       case "approved":
//         return "success.main";
//       case "denied":
//         return "error.main";
//       case "pending":
//       default:
//         return "warning.main";
//     }
//   };

//   // Status filter
//   const filteredViewings = scheduledViewings.filter((viewing) => {
//     const status = (viewing.status || "Pending").toLowerCase();
//     if (currentTab === "pending") return status === "pending";
//     if (currentTab === "approved") return status === "approved";
//     if (currentTab === "denied") return status === "denied";
//     return false;
//   });

//   // Show upcoming (future) and past (older than today) viewings
//   const now = new Date();
//   const upcomingViewings = filteredViewings
//     .filter((viewing) => {
//       // Use dateDetails if possible, fallback to .date
//       const dateStr =
//         viewing.dateDetails?.startTime ||
//         viewing.dateDetails?.date ||
//         viewing.date;
//       if (!dateStr) return true; // If missing, treat as upcoming
//       const d = new Date(dateStr);
//       return d >= now;
//     })
//     .sort((a, b) => {
//       // Sort by scheduled time descending
//       const aStr =
//         a.dateDetails?.startTime ||
//         a.dateDetails?.date ||
//         a.date ||
//         "2099-01-01";
//       const bStr =
//         b.dateDetails?.startTime ||
//         b.dateDetails?.date ||
//         b.date ||
//         "2099-01-01";
//       return new Date(bStr) - new Date(aStr);
//     });

//   const pastViewings = filteredViewings
//     .filter((viewing) => {
//       const dateStr =
//         viewing.dateDetails?.startTime ||
//         viewing.dateDetails?.date ||
//         viewing.date;
//       if (!dateStr) return false;
//       const d = new Date(dateStr);
//       return d < now;
//     })
//     .sort((a, b) => {
//       const aStr =
//         a.dateDetails?.startTime ||
//         a.dateDetails?.date ||
//         a.date ||
//         "1970-01-01";
//       const bStr =
//         b.dateDetails?.startTime ||
//         b.dateDetails?.date ||
//         b.date ||
//         "1970-01-01";
//       return new Date(bStr) - new Date(aStr);
//     });

//   return (
//     <Box sx={{ mt: 4, px: 2 }}>
//       {/* Title */}
//       <Typography variant="h5" sx={{ fontWeight: "bold", color: "primary.main", mb: 3 }}>
//         Your Scheduled Viewings
//       </Typography>

//       {/* Tabs for Status Filtering */}
//       <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
//         <Tab label="Pending" value="pending" />
//         <Tab label="Approved" value="approved" />
//         <Tab label="Denied" value="denied" />
//       </Tabs>

//       {/* Viewing List */}
//       {loading ? (
//         [...Array(3)].map((_, index) => (
//           <Skeleton
//             key={`skeleton-${index}`}
//             variant="rectangular"
//             height={120}
//             sx={{ mb: 2, borderRadius: 2 }}
//           />
//         ))
//       ) : filteredViewings.length === 0 ? (
//         <Typography sx={{ color: "text.secondary", fontStyle: "italic", mt: 2 }}>
//           No viewings for this status
//         </Typography>
//       ) : (
//         <>
//           {/* Upcoming Viewings */}
//           {upcomingViewings.length > 0 && (
//             <List sx={{ maxWidth: 600 }}>
//               {upcomingViewings.map((viewing) => (
//                 <Card
//                   key={viewing.id}
//                   sx={{
//                     mb: 2,
//                     borderRadius: 2,
//                     background: "linear-gradient(135deg, #ffffff 0%, #f9f9f9 100%)",
//                     boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
//                     transition: "transform 0.2s",
//                     "&:hover": { transform: "translateY(-2px)" },
//                   }}
//                 >
//                   <CardContent sx={{ py: 2 }}>
//                     <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
//                       {viewing.listingTitle || "Untitled Listing"}
//                     </Typography>
//                     <Typography sx={{ color: "text.secondary", mb: 0.5 }}>
//                       📍 {viewing.address || "Address TBD"}, {viewing.city || ""}{" "}
//                       {viewing.state || ""}
//                     </Typography>
//                     <Typography sx={{ color: "text.secondary", mb: 0.5 }}>
//                       🗓 {getDisplayDateTime(viewing)}
//                     </Typography>
//                     <Typography
//                       sx={{
//                         fontWeight: "medium",
//                         color: getStatusColor(viewing.status),
//                       }}
//                     >
//                       Status: {viewing.status || "Pending"}
//                     </Typography>
//                   </CardContent>
//                   <Divider />
//                   <CardActions sx={{ p: 1 }}>
//                     <Button
//                       variant="contained"
//                       color="primary"
//                       size="small"
//                       onClick={() => handleViewDetails(viewing.listingId)}
//                       sx={{ borderRadius: 1 }}
//                     >
//                       View Listing
//                     </Button>
//                   </CardActions>
//                 </Card>
//               ))}
//             </List>
//           )}

//           {/* Past Viewings */}
//           {pastViewings.length > 0 && (
//             <Box sx={{ mt: 6 }}>
//               <Typography
//                 variant="h5"
//                 sx={{ fontWeight: "bold", mb: 3, color: "primary.main" }}
//               >
//                 Past {currentTab.charAt(0).toUpperCase() + currentTab.slice(1)} Viewings
//               </Typography>
//               <List sx={{ maxWidth: 600 }}>
//                 {pastViewings.map((viewing) => (
//                   <Card
//                     key={viewing.id}
//                     sx={{
//                       mb: 2,
//                       borderRadius: 2,
//                       background: "linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)",
//                       boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
//                     }}
//                   >
//                     <CardContent sx={{ py: 2 }}>
//                       <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
//                         {viewing.listingTitle || "Untitled Listing"}
//                       </Typography>
//                       <Typography sx={{ color: "text.secondary", mb: 0.5 }}>
//                         📍 {viewing.address || "Address TBD"}, {viewing.city || ""}{" "}
//                         {viewing.state || ""}
//                       </Typography>
//                       <Typography sx={{ color: "text.secondary", mb: 0.5 }}>
//                         🗓 {getDisplayDateTime(viewing)}
//                       </Typography>
//                       <Typography
//                         sx={{
//                           fontWeight: "medium",
//                           color: getStatusColor(viewing.status),
//                         }}
//                       >
//                         Status: {viewing.status || "Pending"}
//                       </Typography>
//                     </CardContent>
//                   </Card>
//                 ))}
//               </List>
//             </Box>
//           )}
//         </>
//       )}
//     </Box>
//   );
// };

// export default UserViewingRequest;



